/**
 * Prefetch-on-hover
 *
 * Bei mouseenter/focusin auf interne <a>-Links injiziert <link rel="prefetch">
 * ins <head>. Beim Klick ist die HTML-Antwort dann schon im Browser-Cache.
 *
 * Connection-aware:
 *  - skip wenn navigator.connection.saveData (Datasaver)
 *  - skip auf 2g / slow-2g
 *  - prefers-reduced-data Media-Query
 *
 * Skip-Regeln:
 *  - extern (different origin)
 *  - target="_blank", download-attr
 *  - hash-only (#anchor)
 *  - data-no-prefetch
 *  - bereits prefetched (Set tracker)
 *
 * Debounce 65ms — verhindert Prefetch bei zufälligem Maus-Vorbeiziehen.
 */

const prefetched = new Set();
const HOVER_DELAY_MS = 65;

function shouldSkipNetwork() {
  if (typeof navigator === "undefined") return true;
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn) {
    if (conn.saveData) return true;
    if (conn.effectiveType === "slow-2g" || conn.effectiveType === "2g") return true;
  }
  if (window.matchMedia?.("(prefers-reduced-data: reduce)").matches) return true;
  return false;
}

function isInternal(url) {
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

function eligible(link) {
  if (!link || !link.href) return false;
  if (link.hasAttribute("download")) return false;
  if (link.target === "_blank") return false;
  if (link.dataset.noPrefetch !== undefined) return false;
  if (link.protocol === "mailto:" || link.protocol === "tel:") return false;

  const u = new URL(link.href, window.location.href);
  if (!isInternal(u.href)) return false;

  // Skip hash-only links
  const currentNoHash = window.location.origin + window.location.pathname;
  const targetNoHash = u.origin + u.pathname;
  if (u.hash && targetNoHash === currentNoHash) return false;

  // Skip current page
  if (u.href === window.location.href) return false;

  return true;
}

function prefetch(href) {
  if (prefetched.has(href)) return;
  prefetched.add(href);

  const link = document.createElement("link");
  link.rel = "prefetch";
  link.href = href;
  link.as = "document";
  link.fetchPriority = "low";
  document.head.appendChild(link);
}

let pendingTimeout = null;
let pendingLink = null;

function schedulePrefetch(event) {
  const link = event.target.closest("a[href]");
  if (!eligible(link)) return;

  clearTimeout(pendingTimeout);
  pendingLink = link;
  pendingTimeout = setTimeout(() => {
    if (pendingLink === link) {
      prefetch(link.href);
    }
  }, HOVER_DELAY_MS);
}

function cancelPrefetch() {
  clearTimeout(pendingTimeout);
  pendingLink = null;
}

export function init() {
  if (shouldSkipNetwork()) return;

  // Mouse-Hover
  document.addEventListener("mouseover", schedulePrefetch, { passive: true });
  document.addEventListener("mouseout", cancelPrefetch, { passive: true });

  // Tab-Focus (Keyboard-User)
  document.addEventListener("focusin", schedulePrefetch);

  // Touch — direkt prefetchen bei touchstart, da Hover nicht greift
  document.addEventListener(
    "touchstart",
    (e) => {
      const link = e.target.closest("a[href]");
      if (eligible(link)) prefetch(link.href);
    },
    { passive: true }
  );
}
